import React, { useEffect, useState } from 'react';
import { 
    DefaultButton, 
    Panel, 
    PanelType,
    IPanelStyles,
    Text,
    Dropdown,
    IDropdownOption,
    TextField,
    SpinButton,
} from '@fluentui/react';
import ErrorMessageComponent from '../error-message/error-message.component';
import ButtonPrimaryComponent from '../button-primary/button-primary.component';
import TableDefaultComponent from '../table-default/table-default.component';
import DynamicFormFieldValueComponent from '../dynamic-form-field-value/dynamic-form-field-value.component';

import ItemFormularioCampo from '../../models/ItemFormularioCampo';
import { ItemFormularioCampoTipo, ItemFormularioCampoTipos } from '../../models/ItemFormularioCampoTipo';

const panelStyles : IPanelStyles = {
    root: {},
    overlay: {},
    hiddenPanel: {},
    main: {},
    commands: {},
    contentInner: {},
    scrollableContent: {},
    navigation: {},
    header: {},
    headerText: {},
    content: {},
    footer: {},
    footerInner: {},
    subComponentStyles: {
        closeButton: {
            root: {
                color: 'rgb(50, 49, 48)',
            },
        },
    },
};

interface IDynamicFormFieldComponentProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (formField: ItemFormularioCampo) => void;
    formField?: ItemFormularioCampo;
};

const DynamicFormFieldComponent = (props: IDynamicFormFieldComponentProps) => {
    const [formField, setFormField] = useState<ItemFormularioCampo>(props.formField ? Object.assign({}, props.formField) : new ItemFormularioCampo());
    const [formFieldValues, setFormFieldValues] = useState<string[]>((props.formField && props.formField.Consulta) ? props.formField.Consulta.split('|') : []);
    const [requireFieldValues, setRequireFieldValues] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [newValueOpen, setNewValueOpen] = useState<boolean>(false);
    const [editValueOpen, setEditValueOpen] = useState<boolean>(false);
    const [selectedIndexValue, setSelectedIndexValue] = useState<number>();

    useEffect(() => {
        if (formField) {
            let formFieldDataType : ItemFormularioCampoTipo | undefined = ItemFormularioCampoTipos.find((ifct: ItemFormularioCampoTipo) => {
                return (ifct.Id === formField.Tipo);
            });
            if (formFieldDataType) {
                setRequireFieldValues(formFieldDataType.RequiereLista);
            }
        }
    }, [formField]);

    useEffect(() => {
        if (formFieldValues) {
            setFormField((formField: ItemFormularioCampo) => {
                formField.Consulta = formFieldValues.join('|');
                return formField;
            });
        }
    }, [formFieldValues]);

    const onChangeDataType = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<ItemFormularioCampoTipo>, index?: number) : void => {
        setFormField({
            ...formField,
            Tipo: (option && option.data) ? option.data.Id : "",
            Solo_lectura: (option && option.data && option.data.SoloLectura) ? true : false,
        });
    };

    const onChangeName = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setFormField({
            ...formField,
            NombreCampo: newValue || "",
        });
    };

    const onChangeOrder = (event: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) : void => {
        setFormField({
            ...formField,
            Orden: newValue ? parseInt(newValue) : 0,
        });
    };

    const onAddValue = (value: string) : void => {
        setFormFieldValues([
            ...formFieldValues,
            value,
        ]);
    };

    const onEditValue = (value: string) : void => {
        if (selectedIndexValue !== undefined) {
            let values : string[] = formFieldValues;
            values.splice(selectedIndexValue, 1);
            setFormFieldValues([
                ...values,
                value,
            ]);
            setSelectedIndexValue(undefined);
        }
    };

    const onDeleteValue = () : void => {
        if (selectedIndexValue !== undefined) {
            let values : string[] = formFieldValues;
            values.splice(selectedIndexValue, 1);
            setFormFieldValues([
                ...values,
            ]);
            setSelectedIndexValue(undefined);
        }
    };

    const onDismiss = () : void => {
        setFormField(new ItemFormularioCampo());
        setFormFieldValues([]);
        setRequireFieldValues(false);
        props.onClose();
    };

    const onSubmit = () : void => {
        if (!formField.Tipo) {
            setError("Seleccione un tipo de dato.")
        }
        else if (!formField.NombreCampo) {
            setError("Complete el nombre del campo.")
        }
        else if (!formField.Orden) {
            setError("Complete el orden del campo.")
        }
        else {
            props.onConfirm(Object.assign({}, formField));
            setFormField(new ItemFormularioCampo());
            props.onClose();
        }
    };

    return (
        <Panel 
            id="ModalCrearFormularioCampo" 
            isOpen={props.isOpen} 
            onDismiss={onDismiss} 
            type={PanelType.largeFixed}
            headerText={props.formField ? 'Modificar campo de formulario' : 'Nuevo campo de formulario'}
            styles={panelStyles}>
            <div className="container-fluid p-0">
                <div className="row m-0">
                    <div className='col-12'>
                        <Text className="my-1 d-block">
                            Tipo de dato:
                        </Text>
                        <Dropdown 
                            selectedKey={formField.Tipo}
                            options={ItemFormularioCampoTipos.map((ifct: ItemFormularioCampoTipo) => {
                                return {
                                    key: ifct.Id,
                                    text: ifct.Nombre,
                                    data: ifct,
                                };
                            })} 
                            onChange={onChangeDataType} 
                            placeholder="Selecciona un tipo de dato" />
                    </div>
                    <div className='col-12'>
                        <Text className="my-1 d-block">
                            Nombre de la columna:
                        </Text>
                        <TextField
                            defaultValue={formField.NombreCampo}
                            onChange={onChangeName} />
                    </div>
                    <div className='col-12'>
                        <Text className="my-1 d-block">
                            Orden:
                        </Text>
                        <SpinButton
                            defaultValue={formField.Orden.toString()}
                            min={1}
                            onChange={onChangeOrder}/>
                    </div>
                    {
                        requireFieldValues ? (
                            <>
                                <div className='col-12'>
                                    <Text className="my-1 d-block">
                                        Valores posibles:
                                    </Text>
                                </div>
                                <div className='col-12'>
                                    <ButtonPrimaryComponent
                                        text="Añadir"
                                        icon="AddTo"
                                        className='me-2'
                                        onClick={() => setNewValueOpen(true)} />
                                    <ButtonPrimaryComponent
                                        text="Editar"
                                        icon="Edit"
                                        disabled={selectedIndexValue === undefined}
                                        className='me-2'
                                        onClick={() => setEditValueOpen(true)} />
                                    <ButtonPrimaryComponent
                                        text="Eliminar"
                                        icon="ChromeClose"
                                        disabled={selectedIndexValue === undefined}
                                        className='me-2'
                                        onClick={onDeleteValue} />
                                </div>
                                <div className='col-12'>
                                    <TableDefaultComponent 
                                        items={formFieldValues.map((value: string) => {
                                            return { 
                                                Valor: value,
                                            };
                                        })}
                                        selectedItem={(selectedIndexValue !== undefined) ? formFieldValues[selectedIndexValue] : undefined}
                                        onRowClick={(value: string, index: number) => setSelectedIndexValue(index)}
                                        onRowDoubleClick={(item: ItemFormularioCampo, index: number) => {
                                            setSelectedIndexValue(index);
                                            setEditValueOpen(true);
                                        }} />
                                </div>
                            </>
                        ) : (
                            null
                        )
                    }
                    <div className='col-12 mt-3'>
                        <DefaultButton 
                            text='Aceptar'
                            iconProps={{
                                iconName: 'Save',
                            }}
                            onClick={onSubmit} />
                    </div>
                </div>
            </div>
            {
                newValueOpen ? (
                    <DynamicFormFieldValueComponent 
                        isOpen={true} 
                        onClose={() => setNewValueOpen(false)}
                        onConfirm={onAddValue} />
                ) : (
                    null
                )
            }
            {
                (editValueOpen && selectedIndexValue !== undefined) ? (
                    <DynamicFormFieldValueComponent 
                        value={formFieldValues[selectedIndexValue]}
                        isOpen={true} 
                        onClose={() => setEditValueOpen(false)}
                        onConfirm={onEditValue} />
                ) : (
                    null
                )
            }
            <ErrorMessageComponent message={error} />
        </Panel>
    );
};

export default DynamicFormFieldComponent;
